// The team's access — who may do what in the company's portal.
//
// A member of the company holds what their roles grant (the built-in roles
// every company has, and the roles the company's admins compose) plus what
// was granted to them directly; an admin member holds everything. The server
// (aldoo-na/organizations/access.js, vendors/lib/org-permissions) keeps the
// definitions: one resource per section of the portal, `read` to see it,
// `write` to change it. This file is the portal's side of it: the role
// picker on a member's row, the dialog with a member's own permissions, the
// roles section with the built-in roles explained and the company's own
// roles made and changed.
import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { listOrgRoles, saveOrgRole, deleteOrgRole, setMemberAccess, grantsPermission } from "aldoo-ra/Organizations/api"
import { Section, Field, TextInput, TextArea, Empty, Chip, errorMessage } from "../AdminShared/ui"
import { PromoteMember, DemoteMember } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"

/** The portal's words for the server's resources (the menu's labels) and actions. */
const RESOURCE_LABEL = {
  "vendor-orders": "Orders",
  "vendor-sales": "Sales",
  "vendor-products": "Products",
  "vendor-dashboard": "Dashboard",
  "vendor-company": "Company",
  "vendor-settings": "Settings",
  "vendor-shipping": "Delivery",
  "vendor-payment-methods": "Payment methods",
  "vendor-members": "Members",
  "vendor-payouts": "Payouts",
  "vendor-import": "Import",
  "vendor-feedback": "Ratings & Comments",
}
const ACTION_LABEL = { read: "View", write: "Edit" }
// the role control's option that is no role but the admin membership itself
const ADMIN = "admin"
// the built-in roles' names and descriptions, as the server words them (lib/org-permissions), for t()
const BUILTIN_TEXT = {
  "order-manager": "Order manager",
  "business-analyst": "Business analyst",
  "product-manager": "Product manager",
  "store-admin": "Store admin",
}

// the small round actions of a row, as panel.jsx draws them (not imported from there: panel.jsx imports this file)
const RowAction = ({ onClick, danger, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
      danger
        ? "border-admin_error/40 text-admin_error hover:bg-admin_error hover:text-admin_text_inv hover:border-admin_error"
        : "border-admin_bordered text-admin_text_secondary hover:bg-admin_primary hover:text-admin_text_inv hover:border-admin_primary"
    }`}
  >
    {children}
  </button>
)

export const resourceLabel = (t, resource) => t(RESOURCE_LABEL[resource] || resource)
export const actionLabel = (t, action) => t(ACTION_LABEL[action] || action)
/** A role's name in the reader's language: a built-in one is translated, the company's own is as typed. */
export const roleName = (t, role) => (role?.builtin ? t(BUILTIN_TEXT[role.key] || role.name) : role?.name || role?.key || "")

/** The roles the organization can give — the definitions, the built-in roles and its own — loaded once per org. */
export function useOrgRoles(orgId, enabled = true) {
  const [roles, setRoles] = useState(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!orgId || !enabled) return setRoles(null)
    let alive = true
    listOrgRoles(orgId).then((res) => alive && setRoles(errorMessage(res) ? { error: errorMessage(res), definitions: [], builtin: [], custom: [] } : res))
    return () => {
      alive = false
    }
  }, [orgId, enabled, tick])
  return { roles, reload: () => setTick((n) => n + 1) }
}

const allRoles = (roles) => [...(roles?.builtin || []), ...(roles?.custom || [])]

/**
 * The matrix of permissions: a row per section, a tick for View and for
 * Edit. `value` is what is set here; `inherited` (permission → role name)
 * what a role already grants, shown ticked and left alone. Edit brings View
 * along, and View going takes Edit with it.
 */
function PermissionMatrix({ definitions, value, inherited = new Map(), onChange, disabled = false, testId = "permission-matrix" }) {
  const t = usePortalTexts(COMPANY)
  const has = (p) => value.has(p)
  const toggle = (resource, action) => {
    const next = new Set(value)
    const read = `${resource}:read`
    const write = `${resource}:write`
    const key = `${resource}:${action}`
    if (next.has(key)) {
      next.delete(key)
      if (action === "read") next.delete(write)
    } else {
      next.add(key)
      if (action === "write") next.add(read)
    }
    onChange(next)
  }
  return (
    <div className="rounded-xl border border-admin_bordered/70 overflow-hidden text-sm" data-testid={testId}>
      <div className="grid grid-cols-[1fr_84px_84px] bg-admin_neutral_1/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">
        <span>{t`Section`}</span>
        <span className="text-center">{t`View`}</span>
        <span className="text-center">{t`Edit`}</span>
      </div>
      {definitions.map((def) => (
        <div key={def.resource} className="grid grid-cols-[1fr_84px_84px] items-center px-3 py-1.5 border-t border-admin_bordered/50">
          <span className="min-w-0 truncate" title={def.description}>
            {resourceLabel(t, def.resource)}
          </span>
          {["read", "write"].map((action) => {
            const key = `${def.resource}:${action}`
            const from = inherited.get(key)
            const offered = def.actions.some((a) => a.name === action)
            if (!offered) return <span key={action} />
            return (
              <span key={action} className="flex justify-center" title={from ? t("From the role %s", from) : undefined}>
                <Checkbox
                  variant="admin_compact"
                  isOn={!!from || has(key)}
                  disabled={disabled || !!from}
                  onChange={() => toggle(def.resource, action)}
                  name={`perm-${key}`}
                  className={from ? "opacity-60" : ""}
                />
              </span>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/**
 * The role control on a member's row: one role, picked in place —
 * Administrator among the options for an admin (`isOrgAdmin`), as making
 * an admin or taking one down is the admins' own. An admin member holds
 * everything, so with nobody able to change it their row says so.
 */
export function RoleSelect({ orgId, member, roles, canManage, isOrgAdmin = false, onChanged }) {
  const t = usePortalTexts(COMPANY)
  const [busy, setBusy] = useState(false)
  const isAdmin = member.role === "admin"
  const options = useMemo(() => {
    const list = allRoles(roles).map((role) => ({ value: role.key, name: roleName(t, role), description: role.builtin ? t(role.description) : role.description }))
    if (isOrgAdmin || isAdmin) list.push({ value: ADMIN, name: t`Administrator`, description: t`Everything in the company, the team and its roles included.` })
    return list
  }, [roles, t, isOrgAdmin, isAdmin])
  const current = isAdmin ? ADMIN : (member.roles || [])[0] || null
  const selected = options.filter((o) => o.value === current)
  // a manager who is not an admin hands out roles, never the keys: an admin's row is theirs to read
  if (!canManage || (isAdmin && !isOrgAdmin)) {
    if (isAdmin) return <Chip className="bg-admin_primary/10 text-admin_primary">{t`Administrator`}</Chip>
    if (!selected.length) return <span className="text-xs text-admin_text_secondary italic">{t`No role`}</span>
    return <Chip className="bg-admin_neutral_1 text-admin_text">{selected[0].name}</Chip>
  }
  const change = async (picked) => {
    const first = Array.isArray(picked) ? picked[0] : picked
    const next = first ? first.value : null
    if (next === current) return
    setBusy(true)
    let res
    if (next === ADMIN) res = await PromoteMember(orgId, member.userId)
    else {
      if (isAdmin) res = await DemoteMember(orgId, member.userId)
      if (!errorMessage(res)) res = await setMemberAccess(orgId, member.userId, { roles: next ? [next] : [] })
    }
    setBusy(false)
    const error = errorMessage(res)
    showNotification({ message: error || t`Role saved`, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    onChanged?.()
  }
  return (
    <div className="w-full" data-testid={`member-roles-${member.userId}`} data-value={current || ""}>
      <DropdownSelect
        variant="admin"
        options={options}
        value={selected}
        placeholder={t`No role`}
        onChange={change}
        disabled={busy || !options.length}
        searchEnabled={options.length > 8}
        showCheckbox={false}
        showSelectionTick
        enableResetButton={!isAdmin}
        allowEmptySelection
        fitOptions
        className="w-full"
        styleOverrides={{ selectedText: "text-admin_text truncate" }}
      />
    </div>
  )
}

/**
 * A member's own permissions, beyond their roles: the matrix, with what the
 * roles already grant ticked and explained.
 */
export function MemberAccessDialog({ orgId, member, roles, canManage, onClose, onChanged }) {
  const t = usePortalTexts(COMPANY)
  const [value, setValue] = useState(() => new Set(member.permissions || []))
  const [busy, setBusy] = useState(false)
  const inherited = useMemo(() => {
    const map = new Map()
    for (const role of allRoles(roles)) {
      if (!(member.roles || []).includes(role.key)) continue
      for (const p of role.permissions || []) if (!map.has(p)) map.set(p, roleName(t, role))
    }
    return map
  }, [roles, member, t])
  const save = async () => {
    setBusy(true)
    const res = await setMemberAccess(orgId, member.userId, { permissions: [...value] })
    setBusy(false)
    const error = errorMessage(res)
    showNotification({ message: error || t`Permissions saved`, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    if (error) return
    onChanged?.()
    onClose()
  }
  return (
    <Modal isOpen onClose={onClose} title={t("Permissions of %s", member.name || member.email)} className="max-w-[640px]">
      <div className="flex flex-col gap-4 p-1" data-testid="member-access-dialog">
        <div className="text-sm text-admin_text_secondary">{t`What this member may see and change, on top of what their roles grant. A section they may view is in their menu; one they may edit, they can change.`}</div>
        <PermissionMatrix definitions={roles?.definitions || []} value={value} inherited={inherited} onChange={setValue} disabled={!canManage} />
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          {canManage && (
            <Button variant="admin_primary" onClick={save} disabled={busy} data-testid="member-access-save">
              {t`Save`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

/** One of the company's own roles, made or changed: its name, what it is for, and the matrix. */
function RoleEditorDialog({ orgId, role, definitions, onClose, onSaved }) {
  const t = usePortalTexts(COMPANY)
  const [name, setName] = useState(role?.name || "")
  const [description, setDescription] = useState(role?.description || "")
  const [value, setValue] = useState(() => new Set(role?.permissions || []))
  const [busy, setBusy] = useState(false)
  const save = async () => {
    if (!name.trim()) return showNotification({ message: t`Name the role`, className: "bg-admin_error text-white" })
    setBusy(true)
    const res = await saveOrgRole(orgId, { key: role?.key, name: name.trim(), description: description.trim(), permissions: [...value] })
    setBusy(false)
    const error = errorMessage(res)
    showNotification({ message: error || t`Role saved`, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    if (error) return
    onSaved?.()
    onClose()
  }
  return (
    <Modal isOpen onClose={onClose} title={role ? t`Edit role` : t`New role`} className="max-w-[640px]">
      <div className="flex flex-col gap-4 p-1" data-testid="role-editor-dialog">
        <Field label={t`Name`}>
          <TextInput value={name} onChange={setName} maxLength={80} data-testid="role-name" />
        </Field>
        <Field label={t`Description`} hint={t`Optional. What the role is for, for whoever hands it out.`}>
          <TextArea value={description} onChange={setDescription} rows={2} maxLength={500} data-testid="role-description" />
        </Field>
        <Field label={t`Permissions`}>
          <PermissionMatrix definitions={definitions} value={value} onChange={setValue} testId="role-matrix" />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={save} disabled={busy} data-testid="role-save">
            {t`Save`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * The permissions of one role, in a dialog of their own: the matrix of
 * sections with View and Edit. A built-in role's is read-only; the
 * company's own, its admins (`canEdit`) tick and save here.
 */
function RolePermissionsDialog({ orgId, role, definitions, canEdit, onClose, onSaved }) {
  const t = usePortalTexts(COMPANY)
  const [value, setValue] = useState(() => new Set(role.permissions || []))
  const [busy, setBusy] = useState(false)
  const editable = canEdit && !role.builtin
  const dirty = editable && (value.size !== (role.permissions || []).length || [...value].some((p) => !role.permissions.includes(p)))
  const save = async () => {
    setBusy(true)
    const res = await saveOrgRole(orgId, { key: role.key, name: role.name, description: role.description, permissions: [...value] })
    setBusy(false)
    const error = errorMessage(res)
    showNotification({ message: error || t`Permissions saved`, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    if (error) return
    onSaved?.()
    onClose()
  }
  return (
    <Modal isOpen onClose={onClose} title={t("Permissions of the role %s", roleName(t, role))} className="max-w-[640px]">
      <div className="flex flex-col gap-4 p-1" data-testid="role-permissions-dialog">
        <div className="text-sm text-admin_text_secondary">
          {role.builtin
            ? t`A built-in role is the same in every company and cannot be changed. For a different set of permissions, make a role of the company's own.`
            : editable
              ? t`Tick what members with this role may see and change. A section they may view is in their menu; one they may edit, they can change.`
              : t`What members with this role may see and change.`}
        </div>
        <PermissionMatrix definitions={definitions} value={value} onChange={setValue} disabled={!editable} testId="role-matrix" />
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {editable ? t`Cancel` : t`Close`}
          </Button>
          {editable && (
            <Button variant="admin_primary" onClick={save} disabled={busy || !dirty} data-testid="role-permissions-save">
              {t`Save`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

/**
 * The roles the company gives: the built-in ones, explained, and its own —
 * made, changed and removed by its admins (`canEdit`). A row is the role's
 * name and what it is for; its permissions open in a dialog.
 */
export function RolesSection({ orgId, roles, canEdit, onChanged, embedded = false, editingState }) {
  const t = usePortalTexts(COMPANY)
  // null: closed; false: a new role; a role: that one — the parent's when it holds the New role button
  const own = useState(null)
  const [editing, setEditing] = editingState || own
  const [viewing, setViewing] = useState(null)
  if (!roles) return null
  const definitions = roles.definitions || []
  const remove = (role) =>
    showPromptDialog({
      title: t`Remove role`,
      message: t("Remove the role %s? Members who hold it lose what it grants.", role.name),
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        const res = await deleteOrgRole(orgId, role.key)
        const error = errorMessage(res)
        showNotification({ message: error || t`Role removed`, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
        onChanged?.()
      },
    })
  const sectionsOf = (role) => new Set((role.permissions || []).map((p) => p.split(":")[0])).size
  const Row = ({ role }) => {
    const description = role.builtin ? t(role.description) : role.description
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3" data-testid={`role-${role.key}`}>
        <div className="min-w-0 flex-1 basis-[260px]">
          <div className="flex items-center gap-2">
            <span className="font-medium">{roleName(t, role)}</span>
            {role.builtin && <Chip className="bg-admin_neutral_1 text-admin_text_secondary">{t`Built-in`}</Chip>}
          </div>
          {description && <div className="text-xs text-admin_text_secondary mt-0.5">{description}</div>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="admin_secondary" onClick={() => setViewing(role)} data-testid={`role-${role.key}-permissions`}>
            {t("Permissions (%s)", sectionsOf(role))}
          </Button>
          {!role.builtin && canEdit && (
            <>
              <Button variant="admin_secondary" onClick={() => setEditing(role)}>{t`Edit`}</Button>
              <Button variant="admin_error" onClick={() => remove(role)}>
                {t`Remove`}
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }
  const subtitle = t`A role is a set of permissions a member can be given. The built-in roles are every company's; the admins can add the company's own.`
  const newRole = canEdit && !editingState && (
    <Button variant="admin_secondary" onClick={() => setEditing(false)} data-testid="role-new">
      {t`New role`}
    </Button>
  )
  const list = (
    <>
      <div className="divide-y divide-admin_bordered/60 text-sm">
        {roles.builtin.map((role) => (
          <Row key={role.key} role={role} />
        ))}
        {roles.custom.map((role) => (
          <Row key={role.key} role={role} />
        ))}
      </div>
      {!roles.custom.length && canEdit && <Empty>{t`No roles of the company's own yet`}</Empty>}
    </>
  )
  const dialogs = (
    <>
      {editing !== null && <RoleEditorDialog orgId={orgId} role={editing || null} definitions={definitions} onClose={() => setEditing(null)} onSaved={onChanged} />}
      {viewing && <RolePermissionsDialog orgId={orgId} role={viewing} definitions={definitions} canEdit={canEdit} onClose={() => setViewing(null)} onSaved={onChanged} />}
    </>
  )
  // inside another section (the Team page's Roles tab): the explanation heads the list
  if (embedded) {
    return (
      <>
        <div className="text-sm text-admin_text_secondary max-w-[720px]" data-testid="roles-tab">{subtitle}</div>
        {list}
        {dialogs}
      </>
    )
  }
  return (
    <>
      <Section title={t`Roles`} subtitle={subtitle} actions={newRole}>
        {list}
      </Section>
      {dialogs}
    </>
  )
}
